/**
 * 工具系统（M2-05 / DESIGN.md §7.4）。
 *
 * 工具以**声明**存在：id、图标字形、单字母快捷键、属性 schema、光标。
 * 左工具栏与工具属性栏都由这份注册表生成——UI 里不出现第二份工具清单。
 *
 * 与动作（M2-01）的分工：动作是「做一件事」，工具是「进入一种持续的交互
 * 模式」。单字母快捷键归工具（V/M/B…），带修饰键的归动作（Ctrl+Z…），
 * 两边在 main 的全局键盘处理里按这个次序分发。
 *
 * 分层（CLAUDE.md 规则 4）：本模块在交互层，不 import src/ui。
 */

export type ToolOption =
  | {
      readonly kind: 'number';
      readonly key: string;
      readonly label: string;
      readonly min: number;
      readonly max: number;
      readonly step: number;
      readonly defaultValue: number;
      /** 显示单位（如 px、%）。 */
      readonly unit?: string;
    }
  | {
      readonly kind: 'boolean';
      readonly key: string;
      readonly label: string;
      readonly defaultValue: boolean;
    }
  | {
      readonly kind: 'enum';
      readonly key: string;
      readonly label: string;
      readonly choices: ReadonlyArray<{ readonly value: string; readonly label: string }>;
      readonly defaultValue: string;
      /**
       * `choices` 只是这个 schema 声明时就知道的静态候选（给 UI 在真实候选
       * 加载完成前当兜底、给类型系统一份非空数组）。`dynamic: true` 的选项
       * 真实候选列表在运行时才知道（目前只有 `fontFamily`——内置/用户上传/
       * 系统导入的字体，见 `main.tsx` 的 `choicesOverride`），`setOption`
       * 对这类选项不按 `choices` 校验，只要求非空字符串——不然任何不在
       * 这份静态列表里的动态值（哪怕是已经真实注册、UI 上也确实选得到的
       * 字体）都会被这里拒绝，`ui/toolbar/OptionsBar.tsx` 的动态候选列表
       * 就形同虚设。
       */
      readonly dynamic?: boolean;
    }
  | {
      /**
       * 一排小按钮，点哪个就固定选哪个（跟 `enum` 的下拉框语义一样，只是
       * 渲染成 PS 属性栏最左侧那种"新选区/添加/减去/交叉"图标按钮组，
       * 不是下拉——选区工具（魔棒/快速选择）用这个而不是 `enum`。
       */
      readonly kind: 'buttonGroup';
      readonly key: string;
      readonly label: string;
      readonly choices: ReadonlyArray<{
        readonly value: string;
        readonly label: string;
        readonly title?: string;
      }>;
      readonly defaultValue: string;
    };

export type ToolOptionValue = number | boolean | string;

export interface ToolDefinition {
  readonly id: string;
  /** Adobe 中文版术语（§7.3）。 */
  readonly label: string;
  /** 单字母快捷键（§7.4），大写。 */
  readonly key: string;
  /**
   * 图标名——对应 `ui/icons/Icon.tsx` 的 `IconName`（Lucide 线条图标，ISC 许可）。
   * 类型仍是 `string` 而不是 import 过来的 `IconName`：交互层不能 import UI 层
   * （CLAUDE.md 规则 4），这里只按约定保持字符串取值与那份表一致。
   */
  readonly glyph: string;
  /** CSS cursor 值。 */
  readonly cursor: string;
  readonly options: readonly ToolOption[];
}

/**
 * 选区加/减/交叉这四个模式按钮——PS 属性栏最左侧那组图标按钮的文字版
 * （新选区/添加/减去/交叉），魔棒和快速选择共用同一份 `value`，方便
 * `combineFromModifiers`（marquee.ts）用同一套字符串。按住 Shift/Alt
 * 是临时覆盖，点这几个按钮是改"没按修饰键时的默认模式"，两条路并存，
 * 跟真实 PS 一致。
 */
const SELECTION_COMBINE_CHOICES = [
  { value: 'replace', label: '新选区', title: '新选区（默认，不按修饰键）' },
  { value: 'add', label: '＋', title: '添加到选区（Shift+拖拽）' },
  { value: 'subtract', label: '－', title: '从选区减去（Alt+拖拽）' },
  { value: 'intersect', label: '∩', title: '与选区交叉（Shift+Alt+拖拽）' },
] as const;

/** §7.4 的工具键位表。测试内置同一张表对拍。 */
export const STANDARD_TOOLS: readonly ToolDefinition[] = [
  { id: 'move', label: '移动', key: 'V', glyph: 'move', cursor: 'default', options: [] },
  {
    // 平移逻辑一直都在（空格临时抓手，见 viewport.ts 的 HandTool），这里
    // 只是把它注册成工具栏里能选中、不用按住空格也能拖拽平移的正式工具——
    // 跟真实 Photoshop 一样，H 是抓手（为了腾出这个键，海绵改成了跟真实
    // PS 一致的"长按 O 才能选"，见下面 sponge 的注释）。
    id: 'hand',
    label: '抓手',
    key: 'H',
    glyph: 'hand',
    cursor: 'grab',
    options: [],
  },
  {
    id: 'marquee',
    label: '选框',
    key: 'M',
    glyph: 'square-dashed',
    cursor: 'crosshair',
    options: [
      // 跟魔棒/快速选择同一份组合模式按钮组——之前选框/套索只能靠
      // Shift/Alt 修饰键切换，没有工具栏按钮，是四个选取工具里唯一
      // 不一致的地方，见 combineFromModifiers 的 defaultMode 参数
      { kind: 'buttonGroup', key: 'combine', label: '选区', choices: SELECTION_COMBINE_CHOICES, defaultValue: 'replace' },
      {
        kind: 'enum',
        key: 'shape',
        label: '形状',
        choices: [
          { value: 'rectangle', label: '矩形' },
          { value: 'ellipse', label: '椭圆' },
        ],
        defaultValue: 'rectangle',
      },
      // 样式：正常（自由拖拽）/固定比例（按 styleWidth:styleHeight 锁定
      // 长宽比）/固定大小（忽略拖拽范围，直接摆一个 styleWidth×styleHeight
      // 的框）——参照 Photoshop 选框工具属性栏。styleWidth/styleHeight
      // 只在非"正常"时有意义，main.tsx 按当前样式过滤
      {
        kind: 'enum',
        key: 'style',
        label: '样式',
        choices: [
          { value: 'normal', label: '正常' },
          { value: 'fixedRatio', label: '固定比例' },
          { value: 'fixedSize', label: '固定大小' },
        ],
        defaultValue: 'normal',
      },
      { kind: 'number', key: 'styleWidth', label: '宽度', min: 0.01, max: 20000, step: 1, defaultValue: 1, unit: 'px' },
      { kind: 'number', key: 'styleHeight', label: '高度', min: 0.01, max: 20000, step: 1, defaultValue: 1, unit: 'px' },
      { kind: 'number', key: 'feather', label: '羽化', min: 0, max: 250, step: 1, defaultValue: 0, unit: 'px' },
      // 只对矩形有意义（椭圆没有"角"），属性栏按当前形状把它过滤掉——
      // 见 main.tsx 的 marqueeToolOptionsForShape，同「形状工具」按 kind
      // 过滤选项那套手法
      { kind: 'number', key: 'radius', label: '半径', min: 0, max: 500, step: 1, defaultValue: 0, unit: 'px' },
    ],
  },
  {
    id: 'lasso',
    label: '套索',
    key: 'L',
    glyph: 'lasso',
    cursor: 'crosshair',
    options: [
      { kind: 'buttonGroup', key: 'combine', label: '选区', choices: SELECTION_COMBINE_CHOICES, defaultValue: 'replace' },
      {
        kind: 'enum',
        key: 'mode',
        label: '模式',
        choices: [
          { value: 'freehand', label: '自由' },
          { value: 'polygon', label: '多边形' },
          { value: 'magnetic', label: '磁性' },
        ],
        defaultValue: 'freehand',
      },
      // 下面三项只在「磁性」模式下有意义，main.tsx 的
      // lassoToolOptionsForMode 按当前模式把它们过滤掉——对应真实
      // Photoshop 磁性套索属性栏的"宽度/对比/频率"
      { kind: 'number', key: 'magneticWidth', label: '宽度', min: 1, max: 256, step: 1, defaultValue: 10, unit: 'px' },
      { kind: 'number', key: 'magneticContrast', label: '对比', min: 1, max: 100, step: 1, defaultValue: 10, unit: '%' },
      { kind: 'number', key: 'magneticFrequency', label: '频率', min: 0, max: 100, step: 1, defaultValue: 57 },
    ],
  },
  {
    id: 'quickSelect',
    label: '快速选择',
    key: 'W',
    glyph: 'wand-sparkles',
    cursor: 'crosshair',
    options: [
      {
        kind: 'buttonGroup',
        key: 'combine',
        label: '选区',
        choices: SELECTION_COMBINE_CHOICES,
        // 默认就是"添加"——真实 PS 快速选择工具本来就是拖到哪并到哪，
        // 不需要按 Shift；这跟魔棒的默认"新选区"不一样
        defaultValue: 'add',
      },
      { kind: 'number', key: 'tolerance', label: '容差', min: 0, max: 255, step: 1, defaultValue: 24 },
      { kind: 'boolean', key: 'contiguous', label: '连续', defaultValue: true },
      { kind: 'boolean', key: 'antiAlias', label: '消除锯齿', defaultValue: true },
      // 真实 PS 默认关闭（只认当前图层）；开了才取样全部可见图层合成结果
      { kind: 'boolean', key: 'sampleAllLayers', label: '取样所有图层', defaultValue: false },
    ],
  },
  {
    id: 'magicWand',
    label: '魔棒',
    key: 'D',
    glyph: 'wand',
    cursor: 'crosshair',
    options: [
      {
        kind: 'buttonGroup',
        key: 'combine',
        label: '选区',
        choices: SELECTION_COMBINE_CHOICES,
        defaultValue: 'replace',
      },
      { kind: 'number', key: 'tolerance', label: '容差', min: 0, max: 255, step: 1, defaultValue: 24 },
      { kind: 'boolean', key: 'contiguous', label: '连续', defaultValue: true },
      { kind: 'boolean', key: 'antiAlias', label: '消除锯齿', defaultValue: true },
      { kind: 'boolean', key: 'sampleAllLayers', label: '取样所有图层', defaultValue: false },
    ],
  },
  {
    id: 'crop',
    label: '裁剪',
    key: 'C',
    glyph: 'crop',
    cursor: 'crosshair',
    options: [
      {
        kind: 'enum',
        key: 'aspect',
        label: '比例',
        choices: [
          { value: 'free', label: '不受约束' },
          { value: 'original', label: '原始比例' },
          { value: '1:1', label: '1:1（方形）' },
          { value: '4:3', label: '4:3' },
          { value: '3:2', label: '3:2' },
          { value: '16:9', label: '16:9' },
        ],
        defaultValue: 'free',
      },
      // 宽/高：跟真实 Photoshop 裁剪属性栏一样，除了比例下拉还能直接敲
      // 数字——这两项显示的是当前裁剪框的实时尺寸（装配层特殊处理，见
      // main.tsx 里对 'crop' 的 value/onChange 分支），不是走 ToolManager
      // 记的静态默认值，所以这里的 defaultValue 只是占位，永远不会被读到。
      { kind: 'number', key: 'width', label: '宽度', min: 1, max: 32767, step: 1, defaultValue: 0, unit: 'px' },
      { kind: 'number', key: 'height', label: '高度', min: 1, max: 32767, step: 1, defaultValue: 0, unit: 'px' },
    ],
  },
  {
    id: 'perspectiveCrop',
    label: '透视裁剪',
    key: 'U',
    glyph: 'frame',
    cursor: 'crosshair',
    options: [],
  },
  {
    id: 'vanishingPoint',
    label: '消失点',
    key: 'K',
    glyph: 'grid-3x3',
    cursor: 'crosshair',
    options: [],
  },
  { id: 'eyedropper', label: '吸管', key: 'I', glyph: 'pipette', cursor: 'crosshair', options: [] },
  {
    // 标签跟真实 Photoshop 对齐：这个工具自动探测周围内容、不需要手动
    // 定源（PatchMatch 内容识别填充），对应的是"污点修复画笔"，不是
    // 下面需要 Alt+点击定源的"修复画笔"（healingBrush）——两者中文名
    // 之前混用过，容易跟真正的修复画笔搞混。
    id: 'heal',
    label: '污点修复画笔',
    key: 'J',
    glyph: 'bandage',
    cursor: 'crosshair',
    options: [
      { kind: 'number', key: 'size', label: '大小', min: 1, max: 500, step: 1, defaultValue: 30, unit: 'px' },
      { kind: 'number', key: 'hardness', label: '硬度', min: 0, max: 100, step: 1, defaultValue: 100, unit: '%' },
    ],
  },
  {
    id: 'healingBrush',
    label: '修复画笔',
    key: 'Y',
    glyph: 'bandage',
    cursor: 'crosshair',
    options: [
      { kind: 'number', key: 'size', label: '大小', min: 1, max: 500, step: 1, defaultValue: 30, unit: 'px' },
      { kind: 'number', key: 'hardness', label: '硬度', min: 0, max: 100, step: 1, defaultValue: 100, unit: '%' },
    ],
  },
  {
    id: 'patch',
    label: '修补',
    // 'X' 是 PS 标配的"交换前景/背景色"全局键（main.tsx 里独立处理，
    // 不属于本注册表的单字母工具键位），字母表里能用的键位这时只剩 'A' 了
    key: 'A',
    glyph: 'puzzle',
    cursor: 'crosshair',
    options: [],
  },
  {
    id: 'redEye',
    label: '红眼',
    key: 'Q',
    glyph: 'eye',
    cursor: 'crosshair',
    options: [
      { kind: 'number', key: 'size', label: '瞳孔大小', min: 4, max: 400, step: 1, defaultValue: 40, unit: 'px' },
    ],
  },
  {
    id: 'brush',
    label: '画笔',
    key: 'B',
    glyph: 'paintbrush',
    cursor: 'crosshair',
    options: [
      { kind: 'number', key: 'size', label: '大小', min: 1, max: 500, step: 1, defaultValue: 30, unit: 'px' },
      { kind: 'number', key: 'hardness', label: '硬度', min: 0, max: 100, step: 1, defaultValue: 100, unit: '%' },
      { kind: 'number', key: 'opacity', label: '不透明度', min: 1, max: 100, step: 1, defaultValue: 100, unit: '%' },
      { kind: 'number', key: 'flow', label: '流量', min: 1, max: 100, step: 1, defaultValue: 100, unit: '%' },
    ],
  },
  {
    id: 'stamp',
    label: '仿制图章',
    key: 'S',
    glyph: 'stamp',
    cursor: 'crosshair',
    options: [
      { kind: 'number', key: 'size', label: '大小', min: 1, max: 500, step: 1, defaultValue: 30, unit: 'px' },
      { kind: 'number', key: 'hardness', label: '硬度', min: 0, max: 100, step: 1, defaultValue: 100, unit: '%' },
      { kind: 'number', key: 'opacity', label: '不透明度', min: 1, max: 100, step: 1, defaultValue: 100, unit: '%' },
    ],
  },
  {
    id: 'eraser',
    label: '橡皮擦',
    key: 'E',
    glyph: 'eraser',
    cursor: 'crosshair',
    options: [
      { kind: 'number', key: 'size', label: '大小', min: 1, max: 500, step: 1, defaultValue: 30, unit: 'px' },
      { kind: 'number', key: 'hardness', label: '硬度', min: 0, max: 100, step: 1, defaultValue: 100, unit: '%' },
    ],
  },
  {
    id: 'gradient',
    label: '渐变',
    key: 'G',
    glyph: 'gradient',
    cursor: 'crosshair',
    options: [
      {
        kind: 'enum',
        key: 'type',
        label: '类型',
        choices: [
          { value: 'linear', label: '线性' },
          { value: 'radial', label: '径向' },
          { value: 'angle', label: '角度' },
          { value: 'reflected', label: '对称' },
          { value: 'diamond', label: '菱形' },
        ],
        defaultValue: 'linear',
      },
      { kind: 'boolean', key: 'reverse', label: '反向', defaultValue: false },
      {
        kind: 'enum',
        key: 'blendMode',
        label: '模式',
        // 十二种可分离模式（gpu/shaders/blend/modes.ts 的 SEPARABLE_BLEND_MODES）；
        // 中文标签在这里硬写一份而不是 import ui/layers 那份表——交互层不能反向
        // 依赖 UI 层（CLAUDE.md 规则 4），两份表用同一批英文 value 保持同步。
        choices: [
          { value: 'normal', label: '正常' },
          { value: 'multiply', label: '正片叠底' },
          { value: 'screen', label: '滤色' },
          { value: 'overlay', label: '叠加' },
          { value: 'darken', label: '变暗' },
          { value: 'lighten', label: '变亮' },
          { value: 'colorDodge', label: '颜色减淡' },
          { value: 'colorBurn', label: '颜色加深' },
          { value: 'hardLight', label: '强光' },
          { value: 'softLight', label: '柔光' },
          { value: 'difference', label: '差值' },
          { value: 'exclusion', label: '排除' },
        ],
        defaultValue: 'normal',
      },
      { kind: 'number', key: 'opacity', label: '不透明度', min: 1, max: 100, step: 1, defaultValue: 100, unit: '%' },
      { kind: 'boolean', key: 'dither', label: '仿色', defaultValue: false },
    ],
  },
  {
    id: 'paintBucket',
    label: '油漆桶',
    key: 'F',
    glyph: 'paint-bucket',
    cursor: 'crosshair',
    options: [
      { kind: 'number', key: 'tolerance', label: '容差', min: 0, max: 255, step: 1, defaultValue: 32 },
      { kind: 'boolean', key: 'contiguous', label: '连续', defaultValue: true },
    ],
  },
  {
    id: 'dodge',
    label: '减淡',
    key: 'O',
    glyph: 'sun-dim',
    cursor: 'crosshair',
    options: [
      { kind: 'number', key: 'size', label: '大小', min: 1, max: 500, step: 1, defaultValue: 30, unit: 'px' },
      { kind: 'number', key: 'hardness', label: '硬度', min: 0, max: 100, step: 1, defaultValue: 100, unit: '%' },
      // 跟真实 PS 的减淡/加深工具选项栏一致：范围决定这一笔主要作用在
      // 阴影/中间调/高光哪一段亮度上，不是不分青红皂白整块提亮/压暗
      // （之前完全没有这个选项，效果显得又平又假）
      {
        kind: 'enum',
        key: 'range',
        label: '范围',
        choices: [
          { value: 'shadows', label: '阴影' },
          { value: 'midtones', label: '中间调' },
          { value: 'highlights', label: '高光' },
        ],
        defaultValue: 'midtones',
      },
      { kind: 'number', key: 'exposure', label: '曝光度', min: 1, max: 100, step: 1, defaultValue: 50, unit: '%' },
    ],
  },
  {
    id: 'burn',
    label: '加深',
    key: 'N',
    glyph: 'flame',
    cursor: 'crosshair',
    options: [
      { kind: 'number', key: 'size', label: '大小', min: 1, max: 500, step: 1, defaultValue: 30, unit: 'px' },
      { kind: 'number', key: 'hardness', label: '硬度', min: 0, max: 100, step: 1, defaultValue: 100, unit: '%' },
      {
        kind: 'enum',
        key: 'range',
        label: '范围',
        choices: [
          { value: 'shadows', label: '阴影' },
          { value: 'midtones', label: '中间调' },
          { value: 'highlights', label: '高光' },
        ],
        defaultValue: 'midtones',
      },
      { kind: 'number', key: 'exposure', label: '曝光度', min: 1, max: 100, step: 1, defaultValue: 50, unit: '%' },
    ],
  },
  {
    id: 'sponge',
    label: '海绵',
    // 26 个字母已经被其余 25 个工具占满（X 留给全局的前景/背景色交换键，
    // 不能挪作工具键），加入抓手（H，真实 PS 里就是这个键）之后再没有
    // 空字母能给海绵。真实 Photoshop 里减淡/加深/海绵本来就没有各自独立
    // 的单字母键，是共享同一个 O、长按/Shift+O 循环——这里就还原成这个
    // 真实语义：不给它一个会跟别的工具打架的假字母键，只能从工具栏
    // 长按"减淡"弹出的分组里选（见下面 TOOL_GROUPS 的 'dodge' 分组）。
    // 这个字符串长度 ≠ 1，ToolManager.handleKey 的单字符判断保证它永远
    // 不会被真实按键命中，纯粹只是给工具提示条显示用。
    key: '长按 O',
    glyph: 'contrast',
    cursor: 'crosshair',
    options: [
      { kind: 'number', key: 'size', label: '大小', min: 1, max: 500, step: 1, defaultValue: 30, unit: 'px' },
      { kind: 'number', key: 'hardness', label: '硬度', min: 0, max: 100, step: 1, defaultValue: 100, unit: '%' },
      { kind: 'number', key: 'flow', label: '流量', min: 1, max: 100, step: 1, defaultValue: 50, unit: '%' },
      {
        kind: 'enum',
        key: 'type',
        label: '模式',
        choices: [
          { value: 'saturate', label: '加色' },
          { value: 'desaturate', label: '去色' },
        ],
        defaultValue: 'desaturate',
      },
    ],
  },
  {
    id: 'blur',
    label: '模糊',
    // 25 个非 X 字母已经被其余全部工具占满（跟上面 sponge 的处境一样，
    // 只是这次连一个"至少还有一个真实字母"的余地都没有）——模糊/锐化/
    // 涂抹三个都没有能用的单字母键，只能从工具栏长按弹出的分组里选。
    key: '仅工具栏-模糊',
    glyph: 'droplet',
    cursor: 'crosshair',
    options: [
      { kind: 'number', key: 'size', label: '大小', min: 1, max: 500, step: 1, defaultValue: 30, unit: 'px' },
      { kind: 'number', key: 'hardness', label: '硬度', min: 0, max: 100, step: 1, defaultValue: 100, unit: '%' },
      { kind: 'number', key: 'strength', label: '强度', min: 1, max: 100, step: 1, defaultValue: 50, unit: '%' },
    ],
  },
  {
    id: 'sharpen',
    label: '锐化',
    key: '仅工具栏-锐化',
    glyph: 'sparkle',
    cursor: 'crosshair',
    options: [
      { kind: 'number', key: 'size', label: '大小', min: 1, max: 500, step: 1, defaultValue: 30, unit: 'px' },
      { kind: 'number', key: 'hardness', label: '硬度', min: 0, max: 100, step: 1, defaultValue: 100, unit: '%' },
      { kind: 'number', key: 'strength', label: '强度', min: 1, max: 100, step: 1, defaultValue: 50, unit: '%' },
    ],
  },
  {
    id: 'smudge',
    label: '涂抹',
    key: '仅工具栏-涂抹',
    glyph: 'wind',
    cursor: 'crosshair',
    options: [
      { kind: 'number', key: 'size', label: '大小', min: 1, max: 500, step: 1, defaultValue: 30, unit: 'px' },
      { kind: 'number', key: 'hardness', label: '硬度', min: 0, max: 100, step: 1, defaultValue: 100, unit: '%' },
      { kind: 'number', key: 'strength', label: '强度', min: 1, max: 100, step: 1, defaultValue: 50, unit: '%' },
    ],
  },
  {
    id: 'pen',
    label: '钢笔',
    key: 'P',
    glyph: 'pen-tool',
    cursor: 'crosshair',
    options: [
      {
        kind: 'enum',
        key: 'mode',
        label: '模式',
        choices: [
          { value: 'shape', label: '形状' },
          { value: 'path', label: '路径' },
        ],
        defaultValue: 'shape',
      },
    ],
  },
  {
    id: 'shape',
    label: '形状',
    key: 'R',
    glyph: 'square',
    cursor: 'crosshair',
    options: [
      {
        kind: 'enum',
        key: 'kind',
        label: '类型',
        choices: [
          { value: 'rectangle', label: '矩形' },
          { value: 'ellipse', label: '椭圆' },
          { value: 'line', label: '直线' },
        ],
        defaultValue: 'rectangle',
      },
      { kind: 'number', key: 'lineWeight', label: '粗细', min: 1, max: 200, step: 1, defaultValue: 4, unit: 'px' },
      // 只对矩形有意义（椭圆没有"角"，直线也没有），main.tsx 的
      // shapeToolOptionsForKind 按当前 kind 把它过滤掉，同「粗细」只在
      // 直线时才显示的过滤方式
      { kind: 'number', key: 'radius', label: '半径', min: 0, max: 500, step: 1, defaultValue: 0, unit: 'px' },
      // 填充/描边的取色本身（纯色 hex / 渐变色标）不是 ToolOption 能装的
      // 简单值，走属性栏里单独的色块+对话框（main.tsx），跟渐变工具的
      // 颜色预览按钮同一套路；这里只登记"是否用渐变"与描边宽度
      { kind: 'boolean', key: 'fillGradient', label: '渐变填充', defaultValue: false },
      { kind: 'number', key: 'strokeWidth', label: '描边宽度', min: 0, max: 200, step: 1, defaultValue: 2, unit: 'px' },
      { kind: 'boolean', key: 'strokeGradient', label: '渐变描边', defaultValue: false },
    ],
  },
  {
    id: 'text',
    label: '文字',
    key: 'T',
    glyph: 'type',
    cursor: 'text',
    options: [
      { kind: 'number', key: 'fontSize', label: '字号', min: 4, max: 400, step: 1, defaultValue: 24, unit: 'px' },
      {
        kind: 'enum',
        key: 'fontFamily',
        label: '字体',
        // 这份列表只是"内置字体还没加载完成前"的静态兜底（真实候选见
        // main.tsx 的 choicesOverride，读 FontLibrary.families）。族名必须
        // 跟字体文件里真实内嵌的族名一致——`SourceHanSansSC-subset.otf`
        // 这个文件名带 SC，但 fontkit 解析出的真实 familyName 是
        // "Source Han Sans CN"，两者不是一回事；`dynamic: true` 让
        // setOption 不再拿这份静态列表校验，但这里仍然要写对，因为
        // `defaultValue`（首次没有用户选择时的实际取值）就是照这份列表来的
        choices: [
          { value: 'Source Han Sans CN', label: '思源黑体' },
          { value: 'Inter', label: 'Inter' },
          { value: 'JetBrains Mono', label: 'JetBrains Mono' },
        ],
        defaultValue: 'Source Han Sans CN',
        dynamic: true,
      },
      { kind: 'boolean', key: 'fillGradient', label: '渐变填充', defaultValue: false },
      { kind: 'number', key: 'strokeWidth', label: '描边宽度', min: 0, max: 100, step: 1, defaultValue: 0, unit: 'px' },
      { kind: 'boolean', key: 'strokeGradient', label: '渐变描边', defaultValue: false },
    ],
  },
  {
    id: 'zoom',
    label: '缩放',
    key: 'Z',
    glyph: 'zoom-in',
    cursor: 'zoom-in',
    options: [
      // Photoshop 的缩放工具属性栏是一对放大/缩小切换按钮，不是下拉框——
      // 复用现成的 boolean 选项（渲染成勾选框）没有引入新的 ToolOption
      // 种类，跟"这个仓库有的活才引入新东西"的原则一致；样式上不是
      // PS 那种图标按钮，但功能是同一个开关。
      { kind: 'boolean', key: 'zoomOut', label: '缩小', defaultValue: false },
    ],
  },
  {
    id: 'place',
    label: '置入',
    // 拖放导入触发的临时会话，不是用户从工具栏手动选的工具——不进
    // TOOL_GROUPS（工具栏因此不会画出一个按钮），字符串长度 ≠ 1 保证
    // ToolManager.handleKey 永远不会被真实按键命中，同 sponge 的"长按 O"
    // 那条注释是一样的道理。
    key: '拖放触发',
    glyph: 'frame',
    cursor: 'default',
    options: [
      { kind: 'number', key: 'width', label: '宽度', min: 1, max: 20000, step: 1, defaultValue: 100, unit: 'px' },
      { kind: 'number', key: 'height', label: '高度', min: 1, max: 20000, step: 1, defaultValue: 100, unit: 'px' },
      // 默认等比（跟 Photoshop 属性栏默认一致），取消勾选后拖手柄/改
      // 宽高单独一栏都只影响那一个轴，见 PlaceToolController#uniform
      { kind: 'boolean', key: 'uniform', label: '等比', defaultValue: true },
    ],
  },
];

/**
 * 工具栏长按分组的一个变体（Photoshop 惯例：长按工具按钮弹出同组变体）。
 *
 * 变体不一定对应不同的工具 id——矩形/椭圆选框其实是同一个 `marquee` 工具
 * 靠 `shape` 选项区分（M2-05 早先的设计取舍：相近变体做成一个工具+选项，
 * 而不是各自独立注册），所以变体在这里只声明"选中它时应该把哪些选项
 * 覆写成什么值"，不强求每个变体有自己的工具 id。裁剪/透视裁剪则是货真
 * 价实的两个独立工具，`optionOverrides` 留空即可。
 */
export interface ToolGroupMember {
  readonly label: string;
  readonly glyph: string;
  readonly toolId: string;
  readonly optionOverrides?: Readonly<Record<string, ToolOptionValue>>;
}

export interface ToolGroup {
  readonly id: string;
  readonly members: readonly ToolGroupMember[];
}

/**
 * 长按分组表。只收纳"确实有 ≥2 个变体"的工具——单变体分组只会多一个
 * 没用的展开箭头，不是 Photoshop 里那种分组的样子。
 */
export const TOOL_GROUPS: readonly ToolGroup[] = [
  {
    // 跟真实 Photoshop 的 J 键分组一致：污点修复画笔/修复画笔/修补/红眼。
    id: 'heal',
    members: [
      { label: '污点修复画笔工具', glyph: 'bandage', toolId: 'heal' },
      { label: '修复画笔工具', glyph: 'bandage', toolId: 'healingBrush' },
      { label: '修补工具', glyph: 'puzzle', toolId: 'patch' },
      { label: '红眼工具', glyph: 'eye', toolId: 'redEye' },
    ],
  },
  {
    id: 'marquee',
    members: [
      { label: '矩形选框工具', glyph: 'square-dashed', toolId: 'marquee', optionOverrides: { shape: 'rectangle' } },
      { label: '椭圆选框工具', glyph: 'circle-dashed', toolId: 'marquee', optionOverrides: { shape: 'ellipse' } },
    ],
  },
  {
    id: 'lasso',
    members: [
      { label: '套索工具', glyph: 'lasso', toolId: 'lasso', optionOverrides: { mode: 'freehand' } },
      { label: '多边形套索工具', glyph: 'pentagon', toolId: 'lasso', optionOverrides: { mode: 'polygon' } },
    ],
  },
  {
    id: 'crop',
    members: [
      { label: '裁剪工具', glyph: 'crop', toolId: 'crop' },
      { label: '透视裁剪工具', glyph: 'frame', toolId: 'perspectiveCrop' },
    ],
  },
  {
    // 跟真实 Photoshop 的 W 键分组一致：快速选择（拖拽生长）与魔棒（点一下）
    // 挤在同一个槽位里，长按切换。
    id: 'quickSelect',
    members: [
      { label: '快速选择工具', glyph: 'wand-sparkles', toolId: 'quickSelect' },
      { label: '魔棒工具', glyph: 'wand', toolId: 'magicWand' },
    ],
  },
  {
    // 跟真实 Photoshop 的 G 键分组一致：渐变与油漆桶。
    id: 'gradient',
    members: [
      { label: '渐变工具', glyph: 'gradient', toolId: 'gradient' },
      { label: '油漆桶工具', glyph: 'paint-bucket', toolId: 'paintBucket' },
    ],
  },
  {
    id: 'shape',
    members: [
      { label: '矩形工具', glyph: 'square', toolId: 'shape', optionOverrides: { kind: 'rectangle' } },
      { label: '椭圆工具', glyph: 'circle', toolId: 'shape', optionOverrides: { kind: 'ellipse' } },
      { label: '直线工具', glyph: 'minus', toolId: 'shape', optionOverrides: { kind: 'line' } },
    ],
  },
  {
    // 跟真实 Photoshop 的 O 键分组一致：减淡/加深/海绵。
    id: 'dodge',
    members: [
      { label: '减淡工具', glyph: 'sun-dim', toolId: 'dodge' },
      { label: '加深工具', glyph: 'flame', toolId: 'burn' },
      { label: '海绵工具', glyph: 'contrast', toolId: 'sponge' },
    ],
  },
  {
    // 跟真实 Photoshop 的分组一致：模糊/锐化/涂抹。三个都没有能用的单字母键
    // （见各自 `key` 字段的注释；三个 key 字符串必须互不相同——ToolManager
    // 按 key 唯一性校验，哪怕都不是真实单字母键也不能填同一个占位串），
    // 分组本身仍然成立——长按第一个
    // 成员（模糊）在工具栏上的按钮就能弹出另外两个。
    id: 'blur',
    members: [
      { label: '模糊工具', glyph: 'droplet', toolId: 'blur' },
      { label: '锐化工具', glyph: 'sparkle', toolId: 'sharpen' },
      { label: '涂抹工具', glyph: 'wind', toolId: 'smudge' },
    ],
  },
];

/** 键盘事件的最小面。 */
export interface ToolKeyEvent {
  readonly key: string;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  readonly altKey: boolean;
}

/**
 * 判断事件目标是否正在接收文本输入。
 * 在输入框里按 B 是想打字母，不是想切画笔——这是单字母快捷键的第一铁律。
 */
export function isEditableTarget(target: unknown): boolean {
  if (typeof target !== 'object' || target === null) return false;
  const el = target as { tagName?: string; isContentEditable?: boolean };
  if (el.isContentEditable === true) return true;
  const tag = el.tagName?.toUpperCase();
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

export class ToolManager {
  readonly #tools: readonly ToolDefinition[];
  readonly #byKey = new Map<string, ToolDefinition>();
  readonly #byId = new Map<string, ToolDefinition>();
  readonly #values = new Map<string, Map<string, ToolOptionValue>>();
  readonly #listeners = new Set<() => void>();
  #activeId: string;

  constructor(tools: readonly ToolDefinition[] = STANDARD_TOOLS, initial = 'move') {
    this.#tools = tools;
    for (const tool of tools) {
      if (this.#byId.has(tool.id)) throw new Error(`工具 ${tool.id} 重复注册。`);
      if (this.#byKey.has(tool.key)) {
        throw new Error(`快捷键 ${tool.key} 被 ${this.#byKey.get(tool.key)!.id} 与 ${tool.id} 同时占用。`);
      }
      this.#byId.set(tool.id, tool);
      this.#byKey.set(tool.key.toUpperCase(), tool);
    }
    if (!this.#byId.has(initial)) throw new Error(`初始工具 ${initial} 不存在。`);
    this.#activeId = initial;
  }

  get tools(): readonly ToolDefinition[] {
    return this.#tools;
  }

  get active(): ToolDefinition {
    return this.#byId.get(this.#activeId)!;
  }

  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  #notify(): void {
    for (const listener of this.#listeners) listener();
  }

  setActive(id: string): void {
    if (!this.#byId.has(id)) throw new Error(`没有工具 ${id}。`);
    if (this.#activeId === id) return;
    this.#activeId = id;
    this.#notify();
  }

  /**
   * 处理键盘事件。命中并切换返回 true。
   * 带任何修饰键（Ctrl/⌘/Alt）都不算工具键——那是动作或浏览器的领地；
   * 文本输入目标上一律放行。
   */
  handleKey(event: ToolKeyEvent, context: { readonly editingText: boolean }): boolean {
    if (context.editingText) return false;
    if (event.ctrlKey || event.metaKey || event.altKey) return false;
    if (event.key.length !== 1) return false;
    const tool = this.#byKey.get(event.key.toUpperCase());
    if (tool === undefined) return false;
    this.setActive(tool.id);
    return true;
  }

  /** 读工具选项，未设置过时给 schema 默认值。 */
  getOption(toolId: string, key: string): ToolOptionValue {
    const schema = this.#optionSchema(toolId, key);
    const stored = this.#values.get(toolId)?.get(key);
    return stored ?? schema.defaultValue;
  }

  /**
   * 写工具选项。数值按 schema 的 min/max 夹取、按 step 取整；
   * enum 拒绝不在选项里的值——属性栏传错值应当立刻炸在这里，
   * 而不是等到工具用它时画出莫名其妙的结果。
   */
  setOption(toolId: string, key: string, value: ToolOptionValue): void {
    const schema = this.#optionSchema(toolId, key);
    let next: ToolOptionValue;
    switch (schema.kind) {
      case 'number': {
        if (typeof value !== 'number' || !Number.isFinite(value)) {
          throw new TypeError(`选项 ${toolId}.${key} 需要有限数字，收到 ${String(value)}。`);
        }
        const clamped = Math.min(schema.max, Math.max(schema.min, value));
        next = Math.round(clamped / schema.step) * schema.step;
        break;
      }
      case 'boolean': {
        if (typeof value !== 'boolean') {
          throw new TypeError(`选项 ${toolId}.${key} 需要布尔值。`);
        }
        next = value;
        break;
      }
      case 'enum':
      case 'buttonGroup': {
        const dynamic = schema.kind === 'enum' && schema.dynamic === true;
        if (typeof value !== 'string' || value === '') {
          throw new RangeError(`选项 ${toolId}.${key} 不接受 ${String(value)}。`);
        }
        if (!dynamic && !schema.choices.some((c) => c.value === value)) {
          throw new RangeError(`选项 ${toolId}.${key} 不接受 ${String(value)}。`);
        }
        next = value;
        break;
      }
    }
    let bag = this.#values.get(toolId);
    if (bag === undefined) {
      bag = new Map();
      this.#values.set(toolId, bag);
    }
    bag.set(key, next);
    this.#notify();
  }

  #optionSchema(toolId: string, key: string): ToolOption {
    const tool = this.#byId.get(toolId);
    if (tool === undefined) throw new Error(`没有工具 ${toolId}。`);
    const schema = tool.options.find((o) => o.key === key);
    if (schema === undefined) throw new Error(`工具 ${toolId} 没有选项 ${key}。`);
    return schema;
  }
}
